import Button from "@core/ui/Button";
import TextField from "@core/ui/TextField";
import { yupResolver } from "@hookform/resolvers/yup";
import cn from "classnames";
import { trim } from "lodash/fp";
import React from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import * as yup from "yup";

import { checkEmail, CheckEmailStatuses } from "../services/nocd-api";
import GoogleSignInButton from "./GoogleSignInButton";

interface CheckEmailFormProps {
  onSuccess: ({
    email,
    status,
  }: {
    email: string;
    status: CheckEmailStatuses;
  }) => void;
  className?: string;
  heading: string;
  subHeading: string;
}

const schema = yup.object({
  email: yup
    .string()
    .trim()
    .email("Enter a valid email address")
    .required("Enter your email address"),
});
type FormValues = yup.TypeOf<typeof schema>;

export default function CheckEmailForm({
  onSuccess,
  className,
  heading,
  subHeading,
}: CheckEmailFormProps): JSX.Element {
  const form = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: "all",
  });

  const { register, formState } = form;
  const { isSubmitting, errors } = formState;

  const onSubmit = form.handleSubmit(({ email }: FormValues) => {
    const sanitizedEmail = trim(email);

    return checkEmail(sanitizedEmail, "member_portal")
      .then(({ status }) =>
        onSuccess({
          email: sanitizedEmail,
          status,
        })
      )
      .catch((error: Error) => toast.error(error.message));
  });

  return (
    <form onSubmit={onSubmit} className={cn("font-manrope mx-auto", className)}>
      <h2 className="text-22px tablet:text-24px text-center font-poppins font-bold leading-tight">
        {heading}
      </h2>

      <p className="text-16px mt-2 mb-4 text-center text-gray-600">
        {subHeading}
      </p>

      <div>
        <TextField
          classes={{
            input: "!bg-white !border-2 !border-gray-100 py-4",
          }}
          hideLabel
          placeholder="Email"
          id="email"
          name="email"
          type="text"
          label="Email"
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          inputMode="email"
          errorMessage={errors.email?.message}
          {...register("email")}
        />
      </div>

      <div className="flex flex-col items-center gap-2 mt-10">
        <Button
          disabled={isSubmitting || !formState?.isValid}
          type="submit"
          loading={isSubmitting}
          className="h-13 font-semibold text-18px px-6 w-full font-poppins"
          color="indigo"
          rounded="lg"
        >
          Next
        </Button>

        <p className="text-center mx-auto">Or</p>

        <GoogleSignInButton.Bound />
      </div>
    </form>
  );
}
